import { inject, Injectable } from '@angular/core';
import {
  ICompareWordsDto,
  ICompareWordsResponseDto,
  IEvaluateShortAnswerFromTextDto,
  IEvaluateShortAnswerResponseDto,
  IExplainTextDto,
  IExplainTextResponseDto,
  IExtractWordsDto,
  IGenerateQuizFromWordsDto,
  IGenerateShortAnswerFromTextDto,
  IGenerateShortAnswerFromTextResponseDto,
  IGenerateStoryDto,
  IGenerateStoryResponseDto,
  IGetEtymologyDto,
  IGetEtymologyResponseDto,
  IGetTopicWordsDto,
  IGetTopicWordsResponseDto,
  IPromptLLMDto,
  IPromptLLMResponseDto,
  IQuizResponseDto,
  IRephraseSentenceDto,
  IRephraseSentenceResponseDto,
  ITranslateDto,
  ITranslateResponseDto,
  ITranslateWordDto,
  ITranslateWordResponseDto,
} from '@shared';
import { Observable } from 'rxjs';

import { GeminiApiService } from '../../../core/services/gemini-api.service';
import { LLMApiService } from '../../../core/services/llm-api.service';
import { ProfileService } from '../../profile/data-access/profile.service';

import { AiGatewayService } from './ai-gateway.service';

@Injectable()
export class DefaultAiGatewayService implements AiGatewayService {
  readonly #profileService = inject(ProfileService);
  readonly #llmApiService = inject(LLMApiService);
  readonly #geminiApiService = inject(GeminiApiService);

  prompt(payload: IPromptLLMDto): Observable<IPromptLLMResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.prompt(payload);
    }
    return this.#geminiApiService.prompt(payload);
  }

  getEtymology(
    payload: IGetEtymologyDto,
  ): Observable<IGetEtymologyResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.getEtymology(payload);
    }
    return this.#geminiApiService.getEtymology(payload);
  }

  generateStory(
    payload: IGenerateStoryDto,
  ): Observable<IGenerateStoryResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.generateStory(payload);
    }
    return this.#geminiApiService.generateStory(payload);
  }

  translateWord(
    payload: ITranslateWordDto,
  ): Observable<ITranslateWordResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.translateWord(payload);
    }
    return this.#geminiApiService.translateWord(payload);
  }

  compareWords(
    payload: ICompareWordsDto,
  ): Observable<ICompareWordsResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.compareWords(payload);
    }
    return this.#geminiApiService.compareWords(payload);
  }

  getTopicWords(
    payload: IGetTopicWordsDto,
  ): Observable<IGetTopicWordsResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.getTopicWords(payload);
    }
    return this.#geminiApiService.getTopicWords(payload);
  }

  rephraseSentence(
    payload: IRephraseSentenceDto,
  ): Observable<IRephraseSentenceResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.rephraseSentence(payload);
    }
    return this.#geminiApiService.rephraseSentence(payload);
  }

  translate(payload: ITranslateDto): Observable<ITranslateResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.translate(payload);
    }
    return this.#geminiApiService.translate(payload);
  }

  explainText(payload: IExplainTextDto): Observable<IExplainTextResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.explainText(payload);
    }
    return this.#geminiApiService.explainText(payload);
  }

  extractWords(
    payload: IExtractWordsDto,
  ): Observable<IGetTopicWordsResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.extractWords(payload);
    }
    return this.#geminiApiService.extractWords(payload);
  }

  generateShortAnswerFromText(
    payload: IGenerateShortAnswerFromTextDto,
  ): Observable<IGenerateShortAnswerFromTextResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.generateShortAnswerFromText(payload);
    }
    return this.#geminiApiService.generateShortAnswerFromText(payload);
  }

  evaluateShortAnswerFromText(
    payload: IEvaluateShortAnswerFromTextDto,
  ): Observable<IEvaluateShortAnswerResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.evaluateShortAnswerFromText(payload);
    }
    return this.#geminiApiService.evaluateShortAnswerFromText(payload);
  }

  generateQuiz(
    payload: IGenerateQuizFromWordsDto,
  ): Observable<IQuizResponseDto> {
    if (this.#shouldUseOwnLlm()) {
      return this.#llmApiService.generateQuiz(payload);
    }
    return this.#geminiApiService.generateQuiz(payload);
  }

  #shouldUseOwnLlm(): boolean {
    const useOwnLlm = this.#profileService.useOwnLlm();
    const llmUrl = this.#profileService.llmUrl();
    const llmModelName = this.#profileService.llmModelName();
    return useOwnLlm && !!llmUrl && !!llmModelName;
  }
}
